import React, { useEffect, useMemo } from 'react'
import { LucideIcon } from 'lucide-react'
import type { Tab } from '../../../hooks/multiWindow/useTabManager'
import { useAutoScrollbar } from '../../../hooks/useAutoScrollbar'
import { PersonalSidebarContent } from './PersonalSidebarContent'
import { WorkSidebarContent } from './WorkSidebarContent'
import { HomeSidebarContent } from './HomeSidebarContent'
import { AllSpacesSidebarContent } from './AllSpacesSidebarContent'
import { SidebarCollapsedProvider } from './SidebarContext'
import { TooltipProvider } from '../../ui/tooltip'
import { HappyModusDropdown } from './HappyModusDropdown'
import './StandardSidebar.scss'

export interface StandardSidebarProps {
  isOpen: boolean
  onToggle: () => void
  lifeSpace: string
  activeTab?: Tab
  tabs?: Tab[]
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: LucideIcon }) => void
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

const StandardSidebarComponent: React.FC<StandardSidebarProps> = ({
  isOpen,
  onToggle,
  lifeSpace,
  activeTab,
  tabs = [],
  openTab,
  navigateActiveTab,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openPreviewTab,
  promotePreviewBySpace
}) => {

  // Memoize sidebar content based on lifeSpace and activeTab
  const sidebarContent = useMemo(() => {
    switch (lifeSpace) {
      case 'personal':
        return <PersonalSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      case 'work':
        return <WorkSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      case 'home':
        return <HomeSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      case 'all':
        return <AllSpacesSidebarContent activeTab={activeTab} openTab={openTab} navigateActiveTab={navigateActiveTab} tabs={tabs} onCloseTabByPath={onCloseTabByPath} onUnpinByPath={onUnpinByPath} onPinByPath={onPinByPath} openPreviewTab={openPreviewTab} promotePreviewBySpace={promotePreviewBySpace} />
      default:
        // Custom Life Space - dynamic content
        return (
          <div className="standard-sidebar__custom-content">
            <p>Custom Life Space: {lifeSpace}</p>
          </div>
        )
    }
  }, [lifeSpace, activeTab, openTab, navigateActiveTab, tabs, onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace])

  // Memoize CSS class
  const sidebarClassName = useMemo(
    () => `standard-sidebar ${!isOpen ? 'standard-sidebar--collapsed' : ''}`,
    [isOpen]
  )

  // Auto-hiding scrollbar with fade indicators
  const { containerRef, scrollbarVisible, showTopFade, showBottomFade, onScroll, flash } = useAutoScrollbar()

  // Flash scrollbar on space change
  useEffect(() => { flash() }, [lifeSpace, flash])

  // Scroll to active item if not visible (with 40px offset for fade gradient).
  // When the item has a sub-menu, scroll so the project sits near the top
  // and as many sub-items as possible are visible below it.
  useEffect(() => {
    const path = activeTab?.path
    if (!path) return
    const container = containerRef.current
    if (!container) return
    // Delay one frame so the sub-menu has time to render after auto-expand.
    const rafId = requestAnimationFrame(() => {
      const item = container.querySelector(`[data-path="${CSS.escape(path)}"]`) as HTMLElement | null
      if (!item) return
      // Check if the next sibling is a sub-menu (rendered by ProjectSidebarItem)
      const subMenu = item.nextElementSibling?.classList.contains('sidebar-sub-menu')
        ? item.nextElementSibling as HTMLElement
        : null
      const cRect = container.getBoundingClientRect()
      const iRect = item.getBoundingClientRect()
      const fadeOffset = 40
      if (subMenu) {
        const smRect = subMenu.getBoundingClientRect()
        // Total height: from item top to sub-menu bottom
        const totalHeight = smRect.bottom - iRect.top
        const viewportHeight = cRect.height - fadeOffset * 2
        if (totalHeight <= viewportHeight) {
          // Everything fits: scroll so the full block is visible
          if (iRect.top < cRect.top + fadeOffset) {
            container.scrollTo({ top: container.scrollTop + (iRect.top - cRect.top) - fadeOffset, behavior: 'smooth' })
          } else if (smRect.bottom > cRect.bottom - fadeOffset) {
            container.scrollTo({ top: container.scrollTop + (smRect.bottom - cRect.bottom) + fadeOffset, behavior: 'smooth' })
          }
        } else {
          // Too tall: pin project at the top so sub-items fill remaining space
          container.scrollTo({ top: container.scrollTop + (iRect.top - cRect.top) - fadeOffset, behavior: 'smooth' })
        }
      } else {
        // No sub-menu: simple scroll into view
        if (iRect.top < cRect.top + fadeOffset) {
          container.scrollTo({ top: container.scrollTop + (iRect.top - cRect.top) - fadeOffset, behavior: 'smooth' })
        } else if (iRect.bottom > cRect.bottom - fadeOffset) {
          container.scrollTo({ top: container.scrollTop + (iRect.bottom - cRect.bottom) + fadeOffset, behavior: 'smooth' })
        }
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [activeTab?.path, containerRef])

  // Auto-close on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 900 && isOpen) {
        onToggle()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen, onToggle])

  const isCollapsed = !isOpen

  return (
    <SidebarCollapsedProvider value={isCollapsed}>
      <TooltipProvider delayDuration={isCollapsed ? 600 : 400} skipDelayDuration={300}>
      <div className={sidebarClassName}>
        <HappyModusDropdown />
        <div className="standard-sidebar__scroll-wrapper">
          <div
            key={lifeSpace}
            ref={containerRef}
            data-space={lifeSpace}
            className={`standard-sidebar__content${scrollbarVisible ? ' standard-sidebar__content--scroll-visible' : ''}`}
            onScroll={onScroll}
          >
            {sidebarContent}
          </div>
          {showTopFade && (
            <div className="auto-scrollbar__fade auto-scrollbar__fade--top">
              <svg className="auto-scrollbar__fade-icon" width="16" height="16" viewBox="0 0 16 16">
                <polyline fill="none" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" points="15.5,10.5 8,5.5 0.5,10.5" />
              </svg>
            </div>
          )}
          {showBottomFade && (
            <div className="auto-scrollbar__fade auto-scrollbar__fade--bottom">
              <svg className="auto-scrollbar__fade-icon" width="16" height="16" viewBox="0 0 16 16">
                <polyline fill="none" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" points="15.5,5.5 8,10.5 0.5,5.5" />
              </svg>
            </div>
          )}
        </div>
      </div>
      </TooltipProvider>
    </SidebarCollapsedProvider>
  )
}

// Memoize StandardSidebar to prevent unnecessary re-renders
// Only re-renders when props change
const StandardSidebar = React.memo(StandardSidebarComponent)

export default StandardSidebar